/**
 * Routing Strategy - Single Source of Truth para decisiones de navegación
 *
 * Este archivo centraliza TODA la lógica de routing de la aplicación.
 * Usa detección de plataforma (Capacitor) como fuente principal de verdad,
 * y config.json SOLO para habilitar/deshabilitar módulos completos.
 *
 * Principios:
 * 1. Platform-driven behavior (nativo vs web, mobile vs desktop)
 * 2. Config-driven features (habilitar/deshabilitar módulos)
 * 3. Single Source of Truth (una sola función decide routing)
 */

import {
  isNativeMobile,
  isMobileWidth,
  isDesktopWidth,
} from "global/utils/platformDetector";

/**
 * Breakpoints de la aplicación
 */
export const BREAKPOINTS = {
  MOBILE_MAX: 1024,
  DESKTOP_MIN: 1280,
};

/**
 * Tipos de plataforma
 */
export const PLATFORM_TYPES = {
  NATIVE_MOBILE: "native_mobile", // App iOS/Android
  WEB_MOBILE: "web_mobile", // Navegador móvil (< 1280px)
  WEB_DESKTOP: "web_desktop", // Navegador desktop (>= 1280px)
};

/**
 * Detecta el tipo de plataforma actual
 * @returns {string} Uno de PLATFORM_TYPES
 */
export const detectPlatformType = () => {
  const isNative = isNativeMobile();
  const windowWidth = window.innerWidth;

  if (isNative) {
    return PLATFORM_TYPES.NATIVE_MOBILE;
  }

  if (windowWidth >= BREAKPOINTS.DESKTOP_MIN) {
    return PLATFORM_TYPES.WEB_DESKTOP;
  }

  return PLATFORM_TYPES.WEB_MOBILE;
};

/**
 * Determina si una ruta debe estar disponible según plataforma y config
 * @param {string} routePath - Ruta a verificar (ej: "/autocheckin", "/buscadorTurnos")
 * @param {object} config - Configuración de la aplicación
 * @returns {boolean} - True si la ruta está disponible
 */
export const isRouteAvailable = (routePath, config) => {
  const platformType = detectPlatformType();

  switch (routePath) {
    case "/autocheckin":
      // Autocheckin está disponible en:
      // - App nativa (siempre)
      // - Web móvil (siempre)
      // - Web desktop (NUNCA - se redirige a buscadorTurnos)
      if (platformType === PLATFORM_TYPES.WEB_DESKTOP) {
        return false;
      }
      return config?.opcHome === true;

    case "/buscadorTurnos":
      // Buscador de turnos está disponible en:
      // - Web desktop (siempre)
      // - Web móvil (NUNCA)
      // - App nativa (NUNCA)
      if (platformType !== PLATFORM_TYPES.WEB_DESKTOP) {
        return false;
      }
      return config?.opcBuscadorTurnos !== false; // Default true

    case "/consultamedica":
    case "/estudios":
    case "/laboratorio":
      // Buscadores individuales disponibles en:
      // - Web móvil (via sidebar)
      // - App nativa (via sidebar)
      // - Web desktop (NUNCA - usan buscadorTurnos unificado)
      if (platformType === PLATFORM_TYPES.WEB_DESKTOP) {
        return false;
      }
      return true;

    default:
      // Otras rutas siguen usando config.json como antes
      return true;
  }
};

/**
 * Obtiene la ruta de redirección por defecto después del login
 * Esta es la ÚNICA función que debe decidir a dónde va el usuario
 * @param {object} config - Configuración de la aplicación
 * @param {object} options - Opciones adicionales (beneficios, etc)
 * @returns {string} - Ruta a la que redirigir
 */
export const getDefaultRouteAfterLogin = (config, options = {}) => {
  const platformType = detectPlatformType();
  const { beneficiosEstado = false } = options;

  // Prioridad 1: Beneficios (si está activo)
  if (beneficiosEstado) {
    return "/beneficiosDim";
  }

  // Prioridad 2: Routing basado en plataforma
  switch (platformType) {
    case PLATFORM_TYPES.NATIVE_MOBILE:
      // App nativa → siempre al autocheckin (si está habilitado)
      if (config?.opcHome === true) {
        return "/autocheckin";
      }
      // Fallback si autocheckin deshabilitado
      return getFirstAvailableModule(config);

    case PLATFORM_TYPES.WEB_MOBILE:
      // Web móvil → autocheckin (si está habilitado)
      if (config?.opcHome === true) {
        return "/autocheckin";
      }
      // Fallback si autocheckin deshabilitado
      return getFirstAvailableModule(config);

    case PLATFORM_TYPES.WEB_DESKTOP:
      // Web desktop → buscador de turnos unificado
      if (config?.opcBuscadorTurnos !== false) {
        return "/buscadorTurnos";
      }
      // Fallback si buscador deshabilitado (raro pero posible)
      return getFirstAvailableModule(config);

    default:
      return "/autocheckin";
  }
};

/**
 * Obtiene la ruta del primer módulo disponible según config
 * @param {object} config - Configuración de la aplicación
 * @returns {string} - Ruta del primer módulo disponible
 */
const getFirstAvailableModule = (config) => {
  const availableModules = [
    { key: "opcConsultaMedica", route: "/consultamedica" },
    { key: "opcEstudios", route: "/estudios" },
    { key: "opcLaboratorio", route: "/laboratorio" },
  ];

  for (const module of availableModules) {
    if (config?.[module.key] === true) {
      return module.route;
    }
  }

  return "/autocheckin"; // Último fallback
};

/**
 * Obtiene la ruta de redirección cuando se ingresa por hash
 * @param {object} config - Configuración de la aplicación
 * @param {string} accionValue - Valor del parámetro "accion" en la URL
 * @returns {string} - Ruta a la que redirigir
 */
export const getRouteForHashLogin = (config, accionValue) => {
  const platformType = detectPlatformType();

  // Prioridad 1: Desktop siempre va a buscador de turnos
  if (
    platformType === PLATFORM_TYPES.WEB_DESKTOP &&
    config?.opcBuscadorTurnos !== false
  ) {
    return "/buscadorTurnos";
  }

  // Prioridad 2: Si viene con parámetro de acción, respetarlo
  if (accionValue) {
    if (accionValue === "P") return "/estudios";
    if (accionValue === "L") return "/laboratorio";
    return "/consultamedica"; // Por defecto (acción "C" o cualquier otra)
  }

  // Prioridad 3: Usar lógica por defecto
  return getDefaultRouteAfterLogin(config);
};

/**
 * Determina si debe redirigir desde autocheckin a buscadorTurnos
 * @param {string} currentPath - Ruta actual
 * @param {object} config - Configuración de la aplicación
 * @returns {string|null} - Ruta a la que redirigir, o null si no debe redirigir
 */
export const shouldRedirectFromAutocheckin = (currentPath, config) => {
  // Solo aplica si estamos en /autocheckin o subrutas
  if (!currentPath.startsWith("/autocheckin")) {
    return null;
  }

  const platformType = detectPlatformType();

  // Si es desktop y buscador está disponible, redirigir
  if (
    platformType === PLATFORM_TYPES.WEB_DESKTOP &&
    config?.opcBuscadorTurnos !== false
  ) {
    return "/buscadorTurnos";
  }

  return null;
};

/**
 * Determina si debe redirigir desde buscadorTurnos a autocheckin
 * (inversa de shouldRedirectFromAutocheckin)
 * @param {string} currentPath - Ruta actual
 * @param {object} config - Configuración de la aplicación
 * @returns {string|null} - Ruta a la que redirigir, o null si no debe redirigir
 */
export const shouldRedirectFromBuscadorTurnos = (currentPath, config) => {
  // Solo aplica si estamos en /buscadorTurnos
  if (!currentPath.startsWith("/buscadorTurnos")) {
    return null;
  }

  const platformType = detectPlatformType();

  // Si es mobile/tablet (no desktop) y autocheckin está disponible, redirigir
  if (platformType !== PLATFORM_TYPES.WEB_DESKTOP && config?.opcHome === true) {
    return "/autocheckin";
  }

  return null;
};

/**
 * Utilidad para debugging - mostrar información de plataforma en consola
 */
export const logPlatformInfo = () => {
  const platformType = detectPlatformType();
  const isNative = isNativeMobile();
  const width = window.innerWidth;

  console.group("🔍 Platform Detection");
  console.log("Platform Type:", platformType);
  console.log("Is Native:", isNative);
  console.log("Window Width:", width);
  console.log("Is Mobile Width:", isMobileWidth());
  console.log("Is Desktop Width:", isDesktopWidth());
  console.groupEnd();
};
